<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-subtitle class="d-flex flex-column">
            First Aid Health Check
            <v-progress-linear
              :value="progress"
              height="25"
              :color="getProgressBarColour()"
            >
              <template v-slot:default="{ value }">
                <strong>{{ Math.ceil(value) }}% Complete</strong>
              </template>
            </v-progress-linear>
          </v-card-subtitle>
          <v-card-text>
            <v-row>
              <v-col>
                <v-stepper v-model="currentStep" vertical flat non-linear>
                  <template v-for="(group, index) in form.groups">
                    <v-stepper-step
                      :key="`group-${group.id}`"
                      :step="index + 1"
                      :complete="isGroupComplete(group)"
                      complete-icon="done"
                      edit-icon="done"
                      :color="getGroupStepColour(group)"
                      editable
                    >
                      {{ group.name
                      }}<span class="caption grey--text">{{
                        getGroupProgressText(group)
                      }}</span>
                    </v-stepper-step>

                    <v-stepper-content
                      :key="`step-${group.id}`"
                      :step="index + 1"
                    >
                      <div
                        v-for="item in group.items"
                        :key="item.id"
                      >
                        <v-row v-if="$_shouldDisplayQuestion(item, form)"
                          class="item-row mt-1 mb-2"
                          :class="{ compliance: $_isComplianceQuestion(item, form) }"
                        >
                          <v-col cols="auto">
                            <v-btn-toggle
                              class="ml-2"
                              v-model="item.result"
                              :color="getResultButtonColour(item)"
                              shaped
                            >
                              <v-btn :value="1" @click="submitAnswer(item, 1)">
                                Yes
                              </v-btn>
                              <v-btn :value="0" @click="submitAnswer(item, 0)">
                                No
                              </v-btn>
                            </v-btn-toggle>
                          </v-col>
                          <v-col>
                            <div v-if="$_isComplianceQuestion(item, form)">
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                  <span v-bind="attrs" class="caption warning--text"
                                    v-on="on">
                                    <v-icon class="mr-1" size="medium" color="warning" >privacy_tip</v-icon>Compliance
                                    </span>
                                </template>
                                <span
                                  >This is necessary to ensure a safe workplace.</span
                                >
                              </v-tooltip>
                              <v-divider class="mb-2 mt-1"></v-divider>
                            </div>
                            <div>{{ item.question }}</div>

                            <v-textarea
                              v-if="item.comments || showCommentInput(item)"
                              :id="`${item.id}-comment-input`"
                              v-model="item.comments"
                              class="mt-2"
                              label="Comments"
                              filled
                              :rows="3"
                              auto-grow
                            ></v-textarea>
                            <a
                              v-else
                              href="#"
                              @click.prevent.stop="setShowCommentInput(item)"
                              class="caption"
                              >Add comment</a
                            >
                          </v-col>
                        </v-row>
                      </div>
                      <v-btn
                        v-if="showPreviousStepButton"
                        @click="goToPreviousStep"
                        class="mr-2"
                        text
                      >
                        <v-icon>chevron_left</v-icon> Previous Step
                      </v-btn>
                      <v-btn
                        class="my-4"
                        color="primary"
                        @click="goToNextStep"
                        :disabled="!isGroupComplete(group)"
                      >
                        Continue
                      </v-btn>
                    </v-stepper-content>
                  </template>

                  <v-stepper-step
                    :key="`group-final-notes`"
                    :step="form.groups.length + 1"
                    complete-icon="done"
                    edit-icon="done"
                    editable
                    @click="focusFinalNotes"
                  >
                    Final Notes
                    <span class="caption grey--text">(optional section)</span>
                  </v-stepper-step>

                  <v-stepper-content :step="form.groups.length + 1">
                    <v-textarea
                      filled
                      ref="final-notes-input"
                      v-model="form.comments"
                    ></v-textarea>

                    <v-btn
                      v-if="showPreviousStepButton"
                      @click="goToPreviousStep"
                      class="mr-2"
                      text
                    >
                      <v-icon>chevron_left</v-icon> Previous Step
                    </v-btn>
                    <v-btn class="my-4" color="primary" @click="goToNextStep">
                      Continue
                    </v-btn>
                  </v-stepper-content>

                  <v-stepper-step
                    :key="`group-company-details`"
                    :step="form.groups.length + 2"
                    complete-icon="done"
                    edit-icon="done"
                    editable
                  >
                    Company Details
                    <span class="caption grey--text">(optional section)</span>
                  </v-stepper-step>

                  <v-stepper-content :step="form.groups.length + 2">
                    <v-row>
                      <v-col lg="6" md="12">
                        <v-text-field
                          label="Company Name"
                          v-model="form.companyName"
                          filled
                          hint="Fill out your company name if you want it to show on the final report"
                          persistent-hint
                        >
                        </v-text-field>

                        <v-text-field
                          label="Contact Email Address"
                          filled
                          v-model="form.contactEmailAddress"
                          hint="We will only contact you if you ask us to"
                          persistent-hint
                        ></v-text-field>

                        <v-switch
                          v-model="form.allowContact"
                          label="I would like Your First Aid Team to contact me about my results"
                        >
                        </v-switch>
                      </v-col>
                    </v-row>

                    <v-btn
                      v-if="showPreviousStepButton"
                      @click="goToPreviousStep"
                      class="mr-2"
                      text
                    >
                      <v-icon>chevron_left</v-icon> Previous Step
                    </v-btn>
                    <v-btn
                      :disabled="!allQuestionsAnswered || isSaving"
                      color="primary"
                      @click="save"
                    >
                      {{ isSaving ? "Saving..." : "Complete" }}
                    </v-btn>
                    <div
                      class="caption red--text mt-1"
                      v-if="!allQuestionsAnswered"
                    >
                      
                    </div>

                    <v-alert :value="!allQuestionsAnswered" type="error" icon="mdi-alert" text class="mt-5" outlined >
                      Not all steps have been completed
                    </v-alert>

                     <v-alert :value="allQuestionsAnswered" type="info" text class="mt-5" outlined>
                      This Health Check is a quick guide only and shouldn't be used to confirm compliance and best practice.
                    </v-alert>

                  </v-stepper-content>
                </v-stepper>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions> </v-card-actions>
        </v-card>
      </v-col>
      <v-col> </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  props: {
    healthCheck: {
      type: Object,
      required: true,
    },
    isSaving: {
      type: Boolean,
      required: false
    }
  },
  created() {
    this.form = {
      ...this.healthCheck,
    };

    // Builds array of display data
    this.form.groups.forEach((group) => {
      group.items.forEach((item) => {
        this.displayData.push({
          id: item.id,
          showCommentInput: false,
        });
      });
    });
  },
  data() {
    return {
      currentStep: 1,
      form: {},
      displayData: [],
    };
  },
  computed: {
    progress() {
      return (this.questionsAnswered / this.totalQuestionCount) * 100;
    },
    totalStepCount() {
      return this.healthCheck.groups.length;
    },
    showNextStepButton() {
      return this.currentStep < this.totalStepCount;
    },
    showPreviousStepButton() {
      return this.currentStep > 1;
    },
    allQuestionsAnswered() {
      return this.questionsAnswered == this.totalQuestionCount;
    },
    showCompleteHealthCheckButton() {
      return this.currentStep === this.totalStepCount;
    },
    totalQuestionCount() {
      return this.form.groups.reduce((allTotal, currentGroup) => {
        return currentGroup.items.reduce((groupTotal, currentItem) => {
          return groupTotal + (this.$_shouldDisplayQuestion(currentItem, this.form) ? 1 : 0);
        }, allTotal);
      }, 0);
    },
    questionsAnswered() {
      return this.form.groups.reduce((allTotal, currentGroup) => {
        return currentGroup.items.reduce((groupTotal, currentItem) => {
          const answered = currentItem.result != undefined;
          return groupTotal + (this.$_shouldDisplayQuestion(currentItem, this.form) && answered ? 1 : 0);
        }, allTotal);
      }, 0);
    },
  },
  methods: {
    submitAnswer(item, answer) {
      item.result = answer;
    },
    goToNextStep() {
      this.currentStep = Number(this.currentStep) + 1;
      if (this.currentStep === this.form.groups.length + 1) {
        this.focusFinalNotes();
      }
    },
    goToPreviousStep() {
      this.currentStep = Number(this.currentStep) - 1;
    },
    getGroupProgressText(group) {
      const complete = group.items.filter(
        (item) => this.$_shouldDisplayQuestion(item, this.form) && item.result !== undefined
      ).length;
      const total = group.items.filter(item => this.$_shouldDisplayQuestion(item, this.form)).length;
      
      return `${complete}/${total} complete`;
    },
    getProgressBarColour() {
      return this.allQuestionsAnswered ? "success" : "#db3426";
    },
    isGroupComplete(group) {
      return !group.items.some((item) => this.$_shouldDisplayQuestion(item, this.form) && item.result === undefined);
    },
    getGroupStepColour(group) {
      return this.isGroupComplete(group) ? "success" : "#db3426";
    },
    getResultButtonColour({ questionType, result }) {
      if (questionType === 2) return "default";
      return result ? "success": "error";
    },
    setShowCommentInput(item) {
      const foundDisplayData = this.displayData.find((x) => x.id === item.id);
      if (!foundDisplayData) return;

      foundDisplayData.showCommentInput = true;

      this.$nextTick(() => {
        document.getElementById(`${item.id}-comment-input`).focus();
      });
    },
    showCommentInput(item) {
      const foundDisplayData = this.displayData.find((x) => x.id === item.id);
      if (!foundDisplayData) return false;

      return foundDisplayData.showCommentInput;
    },
    focusFinalNotes() {
      this.$nextTick(() => {
        this.$refs["final-notes-input"].focus();
      });
    },
    save() {
      if (confirm("Are you sure you want to complete?")) {
        this.$emit("onSave", this.form);
      }
      
    },
  },
};
</script>

<style scoped>
.item-row.compliance {
  background-color: rgba(265, 165, 0, 0.1);
  /* padding-top: 8px; */
}
</style>